"use client"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import "./ProductDetailManager.css"
import productService from "../../../services/api/productService"
import productCategoryService from "../../../services/api/productCategoryService"

export default function ProductDetailManager({ onBack, onDelete, onUpdate }) {
    const { id } = useParams() // Lấy ID sản phẩm từ URL
    const navigate = useNavigate()
    const [isEditing, setIsEditing] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [categories, setCategories] = useState([])
    const [product, setProduct] = useState(null) // Thêm state cho product
    const [formData, setFormData] = useState({
        productname: "",
        photo_title: "",
        product_img: [],
        product_category: "",
    })

    // Tải dữ liệu sản phẩm khi có ID
    useEffect(() => {
        if (id) {
            const fetchProduct = async () => {
                try {
                    setLoading(true)
                    const response = await productService.getProductById(id)
                    setProduct(response)
                    setFormData({
                        productname: response.productname || "",
                        photo_title: response.photo_title || "",
                        product_img: response.product_img || [],
                        product_category: response.product_category?._id || "",
                    })
                } catch (error) {
                    console.error("Lỗi khi tải sản phẩm:", error)
                    setError("Không thể tải sản phẩm")
                } finally {
                    setLoading(false)
                }
            }
            fetchProduct()
        }
    }, [id])

    // Tải danh mục sản phẩm
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await productCategoryService.getAllCategories()
                setCategories(Array.isArray(response) ? response : [])
            } catch (error) {
                console.error("Lỗi khi tải danh mục:", error)
                setError("Không thể tải danh mục sản phẩm")
            }
        }
        fetchCategories()
    }, [])

    // Xử lý khi product không tồn tại hoặc đang tải
    if (loading) {
        return (
            <div className="product-detail-container">
                <div className="product-detail-card">
                    <h2>Đang tải sản phẩm...</h2>
                </div>
            </div>
        )
    }

    if (!product) {
        return (
            <div className="product-detail-container">
                <div className="product-detail-card">
                    <h2>Không Tìm Thấy Sản Phẩm</h2>
                    <button className="back-button" onClick={() => navigate("/manager/products")}>
                        Quay Lại Danh Sách
                    </button>
                </div>
            </div>
        )
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files)
            const newImages = []

            files.forEach(file => {
                const reader = new FileReader()
                reader.onload = (event) => {
                    if (event.target && event.target.result) {
                        const img = new Image()
                        img.src = event.target.result
                        img.onload = () => {
                            const canvas = document.createElement('canvas')
                            const MAX_WIDTH = 800
                            const MAX_HEIGHT = 800
                            let width = img.width
                            let height = img.height

                            if (width > height) {
                                if (width > MAX_WIDTH) {
                                    height *= MAX_WIDTH / width
                                    width = MAX_WIDTH
                                }
                            } else {
                                if (height > MAX_HEIGHT) {
                                    width *= MAX_HEIGHT / height
                                    height = MAX_HEIGHT
                                }
                            }

                            canvas.width = width
                            canvas.height = height
                            const ctx = canvas.getContext('2d')
                            ctx.drawImage(img, 0, 0, width, height)

                            const compressedImage = canvas.toDataURL('image/jpeg', 0.7)
                            newImages.push(compressedImage)

                            if (newImages.length === files.length) {
                                setFormData(prev => ({
                                    ...prev,
                                    product_img: [...prev.product_img, ...newImages]
                                }))
                            }
                        }
                    }
                }
                reader.readAsDataURL(file)
            })
        }
    }

    const handleTitleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    const img = new Image()
                    img.src = event.target.result
                    img.onload = () => {
                        const canvas = document.createElement('canvas')
                        const MAX_WIDTH = 800
                        const MAX_HEIGHT = 800
                        let width = img.width
                        let height = img.height

                        if (width > height) {
                            if (width > MAX_WIDTH) {
                                height *= MAX_WIDTH / width
                                width = MAX_WIDTH
                            }
                        } else {
                            if (height > MAX_HEIGHT) {
                                width *= MAX_HEIGHT / height
                                height = MAX_HEIGHT
                            }
                        }

                        canvas.width = width
                        canvas.height = height
                        const ctx = canvas.getContext('2d')
                        ctx.drawImage(img, 0, 0, width, height)

                        const compressedImage = canvas.toDataURL('image/jpeg', 0.7)
                        setFormData(prev => ({
                            ...prev,
                            photo_title: compressedImage
                        }))
                    }
                }
            }
            reader.readAsDataURL(file)
        }
    }

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            product_img: prev.product_img.filter((_, i) => i !== index)
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            const updatedProduct = await productService.updateProduct(product._id, formData)
            setProduct(updatedProduct) // Cập nhật lại product
            setIsEditing(false)
            if (onUpdate) {
                onUpdate(updatedProduct)
            }
        } catch (error) {
            console.error("Lỗi khi cập nhật sản phẩm:", error)
            setError("Không thể cập nhật sản phẩm")
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="product-detail-container">
            <div className="product-detail-card">
                <div className="detail-header">
                    <h2>Chi Tiết Sản Phẩm</h2>
                    <div className="header-actions">
                        {isEditing ? (
                            <button className="cancel-button" onClick={() => setIsEditing(false)}>
                                Hủy
                            </button>
                        ) : (
                            <button className="edit-button" onClick={() => setIsEditing(true)}>
                                Chỉnh Sửa
                            </button>
                        )}
                        <button className="back-button" onClick={() => navigate("/manager/products")}>
                            Quay Lại Danh Sách
                        </button>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}
                {loading && <div className="loading-spinner">Đang xử lý...</div>}

                <div className="detail-content">
                    {isEditing ? (
                        <form onSubmit={handleSubmit} className="edit-form">
                            <div className="form-group">
                                <label htmlFor="productname">Tên Sản Phẩm</label>
                                <input
                                    type="text"
                                    id="productname"
                                    name="productname"
                                    value={formData.productname}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="product_category">Danh Mục</label>
                                <select
                                    id="product_category"
                                    name="product_category"
                                    value={formData.product_category}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Chọn danh mục</option>
                                    {categories.map((category) => (
                                        <option key={category._id} value={category._id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="photo_title">Ảnh Tiêu Đề</label>
                                <input
                                    type="file"
                                    id="photo_title"
                                    name="photo_title"
                                    onChange={handleTitleImageChange}
                                    accept="image/*"
                                />
                                <div className="image-preview">
                                    <img src={formData.photo_title || "/placeholder.svg"} alt="Xem trước ảnh tiêu đề" />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="product_img">Ảnh Sản Phẩm</label>
                                <input
                                    type="file"
                                    id="product_img"
                                    name="product_img"
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    multiple
                                />
                                <div className="images-preview">
                                    {formData.product_img.map((img, index) => (
                                        <div key={index} className="image-preview-item">
                                            <img src={img} alt={`Preview ${index + 1}`} />
                                            <button
                                                type="button"
                                                className="remove-image"
                                                onClick={() => handleRemoveImage(index)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    {formData.product_img.length === 0 && (
                                        <div className="image-preview-placeholder">
                                            <img src="/placeholder.svg" alt="No images" />
                                            <p>Chưa có ảnh nào được chọn</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="save-button" disabled={loading}>
                                    {loading ? "Đang lưu..." : "Lưu Thay Đổi"}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className="detail-title-image">
                                <h3>Ảnh Tiêu Đề</h3>
                                <img src={product.photo_title || "/placeholder.svg"} alt="Ảnh tiêu đề" />
                            </div>

                            <div className="detail-info">
                                <div className="info-group">
                                    <label>Tên Sản Phẩm</label>
                                    <div className="info-value">{product.productname}</div>
                                </div>

                                <div className="info-group">
                                    <label>Danh Mục</label>
                                    <div className="info-value">{product.product_category?.name || "Chưa phân loại"}</div>
                                </div>
                            </div>

                            <div className="detail-product-image">
                                <h3>Hình Ảnh Sản Phẩm</h3>
                                <div className="product-image-container">
                                    {Array.isArray(product.product_img) && product.product_img.length > 0 ? (
                                        product.product_img.map((img, index) => (
                                            <img
                                                key={index}
                                                src={img || "/placeholder.svg"}
                                                alt={`${product.productname} - Hình ${index + 1}`}
                                            />
                                        ))
                                    ) : (
                                        <img src="/placeholder.svg" alt="Không có hình ảnh sản phẩm" />
                                    )}
                                </div>
                            </div>

                            <div className="detail-actions">
                                <button className="delete-button" onClick={() => onDelete(product._id)}>
                                    Xóa Sản Phẩm
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}